import type { InboxSectionKey } from '@/app/(home)/inbox/inbox-sections';
import type { InboxPrStatus, InboxPullRequest } from '@/app/(home)/inbox/types';
import type { InboxGroupingInputs } from '@/app/(home)/inbox/utils/pull-requests';
import type { CubicBadgeKind, InboxAutoFixLoop } from '@/components/inbox/pr-inbox-cubic-status';
import type { PullRequestPerson } from '@/components/inbox/pr-reviewers';

import {
  describeCubicWork,
  getCubicReviewerPresentation,
} from '@/app/(home)/inbox/utils/cubic-reviewer-presentation';
import {
  getPrUniqueId,
  groupPullRequests,
  isReturnedToAuthor,
} from '@/app/(home)/inbox/utils/pull-requests';
import { NO_REVIEWER_LABEL } from '@/components/inbox/no-reviewer-mark';
import { getCubicBadge } from '@/components/inbox/pr-inbox-cubic-status';
import { getFirstReviewer, getLatestReviewer, isNobodyOn } from '@/components/inbox/pr-reviewers';

/** The inbox's sections whose next move is the user's: open in the tray, counted on the badge. */
const OPEN_SECTIONS = ['needsYou', 'readyToMerge'] as const satisfies readonly InboxSectionKey[];

/** The inbox's section of the user's pull requests that wait on someone else: folded in the tray. */
const FOLDED_SECTIONS = ['waitingOnOthers'] as const satisfies readonly InboxSectionKey[];

export type DesktopReviewQueueSectionId =
  | (typeof OPEN_SECTIONS)[number]
  | (typeof FOLDED_SECTIONS)[number];

/**
 * A pull request in one of the tray's sections, with what its row needs to know. A stack sits
 * whole in one section, so a member's own status, which decides its row's action as on the inbox's
 * rows, can differ from its section's.
 */
export type DesktopReviewQueueItem = {
  pr: InboxPullRequest;
  status: Exclude<InboxPrStatus, 'merged' | 'closed'>;
  /** Whether the signed-in user wrote the pull request. */
  isMine: boolean;
};

export type DesktopReviewQueueGroup = {
  id: DesktopReviewQueueSectionId;
  items: DesktopReviewQueueItem[];
};

/**
 * The inbox's sections about the user, in its order, without the empty ones. Drafts, Team, Snoozed
 * and Recently merged stay in the inbox.
 */
export type DesktopReviewQueueSections = {
  open: DesktopReviewQueueGroup[];
  folded: DesktopReviewQueueGroup[];
};

/** The desktop app's review queue, shared by the app badge and the tray popover. */
export type DesktopReviewQueue =
  | { status: 'signed-out' }
  | { status: 'loading' }
  | { status: 'error' }
  | ({ status: 'ready' } & DesktopReviewQueueSections);

/**
 * Groups the user's pull requests by the inbox's own rules, checks, stacks and snoozes included,
 * so the tray and the badge hold the pull requests the inbox does.
 */
export function buildDesktopReviewQueueSections(
  pullRequests: InboxPullRequest[],
  username: string,
  inputs: InboxGroupingInputs,
): DesktopReviewQueueSections {
  const grouped = groupPullRequests(pullRequests, username, inputs);
  const toGroups = (ids: readonly DesktopReviewQueueSectionId[]) =>
    ids.flatMap((id) => {
      const items = grouped[id].flatMap((pr): DesktopReviewQueueItem[] => {
        const status = grouped.globalStackInfoMap.get(getPrUniqueId(pr))?.individualStatus ?? id;
        // A stack sits whole in one section, but its merged or closed members wait on nobody.
        if (status === 'merged' || status === 'closed') {
          return [];
        }
        return [{ pr, status, isMine: pr.author.toLowerCase() === username.toLowerCase() }];
      });
      return items.length > 0 ? [{ id, items }] : [];
    });
  return { open: toGroups(OPEN_SECTIONS), folded: toGroups(FOLDED_SECTIONS) };
}

/**
 * What a queue row shows beside its status, and what it says before the pull request's number.
 * A pull request of the user's own is described by its state: a draft, failing checks, nobody on it
 * yet, its reviewer, or cubic while cubic has it. The row's leading avatar always belongs to the
 * pull request's author.
 */
export type ReviewQueueRowSubject =
  | { kind: 'person'; person: PullRequestPerson; reason: string | null }
  | { kind: 'no-reviewer'; reason: string }
  | { kind: 'cubic'; badge: CubicBadgeKind | null; reason: string };

function getAuthor(pr: InboxPullRequest): PullRequestPerson {
  return { login: pr.author, avatarUrl: pr.avatarUrl };
}

/**
 * Falls back to the author when GitHub counts a review the inbox holds no human review for, such
 * as a mirrored bot approval.
 */
function reviewerOrAuthor(
  pr: InboxPullRequest,
  reviewer: PullRequestPerson | null,
  describe: (login: string) => string,
): ReviewQueueRowSubject {
  return reviewer
    ? { kind: 'person', person: reviewer, reason: describe(reviewer.login) }
    : { kind: 'person', person: getAuthor(pr), reason: null };
}

/**
 * Who has a pull request's next move, and why. Someone else's pull request that needs the user's
 * review shows its author. The user's own pull requests and other stack members follow their own
 * state. `autoFix` is the auto-fix loop as the inbox row reads it: while it fixes, what cubic is
 * doing says more than who reviews it. Then come a draft, which a stack can bring in, requested
 * changes, failing checks, auto-fix's failure or the threads it left, an approval, nobody being on
 * it, cubic's review, and last its reviewers.
 */
export function getQueueRowSubject(
  { pr, isMine, status }: DesktopReviewQueueItem,
  autoFix: InboxAutoFixLoop | null,
): ReviewQueueRowSubject {
  if (!isMine && status === 'needsYou') {
    return { kind: 'person', person: getAuthor(pr), reason: pr.author };
  }
  const cubic = (reason: string): ReviewQueueRowSubject => ({
    kind: 'cubic',
    badge: getCubicBadge(getCubicReviewerPresentation(pr.latestCubicReview), autoFix),
    reason,
  });

  if (autoFix?.activity.kind === 'fixing') {
    return cubic(autoFix.description);
  }
  if (pr.draft) {
    return { kind: 'person', person: getAuthor(pr), reason: 'Draft' };
  }
  if (isReturnedToAuthor(pr)) {
    return reviewerOrAuthor(
      pr,
      getLatestReviewer(pr, 'CHANGES_REQUESTED'),
      (login) => `${login} requested changes`,
    );
  }
  if ((pr.checks?.failed ?? 0) > 0) {
    return { kind: 'person', person: getAuthor(pr), reason: 'Checks failing' };
  }
  if (autoFix?.phase === 'failed' || autoFix?.phase === 'threads-left') {
    return cubic(`Auto-fix: ${autoFix.description}`);
  }
  if (pr.reviewState === 'APPROVED') {
    return reviewerOrAuthor(
      pr,
      getLatestReviewer(pr, 'APPROVED'),
      (login) => `approved by ${login}`,
    );
  }
  if (isNobodyOn(pr, autoFix)) {
    return { kind: 'no-reviewer', reason: NO_REVIEWER_LABEL };
  }
  const cubicWork = describeCubicWork(pr.latestCubicReview, autoFix);
  return cubicWork
    ? cubic(cubicWork)
    : reviewerOrAuthor(pr, getFirstReviewer(pr), (login) => login);
}

/** What a row offers on hover: Approve on a review request, Merge on the user's ready PR. */
export type QueueRowAction = 'approve' | 'merge' | null;

/**
 * A row's action follows its pull request's own status, as on the inbox's rows. Someone else's
 * pull request that waits on the user's review can be approved, and the user's own ready one merged.
 */
export function getQueueRowAction({ status, isMine }: DesktopReviewQueueItem): QueueRowAction {
  if (status === 'readyToMerge') {
    return 'merge';
  }
  return status === 'needsYou' && !isMine ? 'approve' : null;
}

/** The badge a folded section shows for cubic: a fix under way, else a review under way. */
export function getBusiestCubicBadge(subjects: ReviewQueueRowSubject[]): CubicBadgeKind | null {
  const badges = subjects.map((subject) => (subject.kind === 'cubic' ? subject.badge : null));
  if (badges.includes('fixing')) {
    return 'fixing';
  }
  return badges.includes('reviewing') ? 'reviewing' : null;
}

/** The people a folded section shows: its rows' people, once each and at most `limit`. */
export function getSectionPeople(
  subjects: ReviewQueueRowSubject[],
  limit: number,
): PullRequestPerson[] {
  const people = new Map<string, PullRequestPerson>();
  for (const subject of subjects) {
    if (subject.kind === 'person' && !people.has(subject.person.login.toLowerCase())) {
      people.set(subject.person.login.toLowerCase(), subject.person);
    }
  }
  return [...people.values()].slice(0, limit);
}

/**
 * The number on the app badge and next to the menu bar icon: the pull requests in the inbox's
 * Needs you and Ready to merge, the tray's open sections. `null` keeps the last known count while
 * the queue is loading or failed to refresh, and signing out clears it.
 */
export function getReviewQueueBadgeCount(queue: DesktopReviewQueue): number | null {
  switch (queue.status) {
    case 'signed-out':
      return 0;
    case 'loading':
    case 'error':
      return null;
    case 'ready':
      return queue.open.reduce((count, group) => count + group.items.length, 0);
  }
}
